import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, FileSpreadsheet, Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { formatDMY } from "@/lib/dmy";
/**
 * PrintPreviewPage - Standalone page for print preview
 * Data is passed via sessionStorage to avoid URL length limits
 */
const PrintPreviewPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const source = searchParams.get("source") || "unknown";
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const storedData = sessionStorage.getItem('printPreviewData');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        setData(parsed);
        // Clear after loading
        sessionStorage.removeItem('printPreviewData');
      }
    } catch (e) {
      console.error("Failed to load print data:", e);
    }
    setLoading(false);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    // For PDF, we'll use browser's print to PDF
    toast.info("Use 'Save as PDF' in the print dialog");
    window.print();
  };

  const handleDownloadExcel = () => {
    if (!data?.errors) return;
    
    // Simple CSV download
    const headers = ['Tracking', 'Client', 'Product', 'Qty', 'Error Details', 'Date', 'Country', 'Service'];
    const rows = data.errors.map(e => [
      e.tracking || '',
      e.client_code || '',
      e.second_name || e.product || '',
      e.product_count || 1,
      (e.errors || []).join('; '),
      e.order_date || '',
      e.country || '',
      e.service || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Costing_Errors_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Downloaded as CSV");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading print preview...</p>
        </div>
      </div>
    );
  }

  if (!data || !data.errors) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md p-6">
          <h2 className="text-xl font-semibold text-slate-700 mb-2">No Data Available</h2>
          <p className="text-slate-500 mb-4">
            The print preview data has expired or was not found.
          </p>
          <Button onClick={() => navigate(-1)} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const { errors, title, filters, generatedAt } = data;
  
  // Calculate summary stats
  const productErrors = errors.filter(e => e.errors?.some(err => err.includes('Product') || err.includes('Price')));
  const shippingErrors = errors.filter(e => e.errors?.some(err => err.includes('Shipping') || err.includes('Service')));
  const weightErrors = errors.filter(e => e.errors?.some(err => err.includes('Weight')));
  const countryErrors = errors.filter(e => e.errors?.some(err => err.includes('Country')));

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      {/* Toolbar - Hidden when printing */}
      <div className="bg-white border-b sticky top-0 z-10 print:hidden">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <h1 className="text-sm font-medium text-slate-700">Print Preview</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleDownloadExcel} className="gap-2">
              <FileSpreadsheet className="w-4 h-4" />
              Download CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="gap-2">
              <Download className="w-4 h-4" />
              Save as PDF
            </Button>
            <Button size="sm" onClick={handlePrint} className="gap-2 bg-blue-600 hover:bg-blue-700">
              <Printer className="w-4 h-4" />
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* Print Content */}
      <div className="max-w-7xl mx-auto p-4 print:p-0 print:max-w-none">
        <div className="bg-white rounded-lg shadow-sm print:shadow-none print:rounded-none">
          {/* Header */}
          <div className="px-6 py-4 border-b print:border-b-2">
            <h1 className="text-xl font-bold text-slate-800">📋 {title || 'Costing Errors Report'}</h1>
            <p className="text-sm text-slate-500 mt-1">
              Generated: {generatedAt ? new Date(generatedAt).toLocaleString() : 'Now'} | 
              Total Records: {errors.length}
              {filters?.client_code && ` | Client: ${filters.client_code}`}
            </p>
          </div>

          {/* Summary Stats */}
          <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4 bg-slate-50 print:bg-gray-100">
            <div className="text-center p-3 bg-white rounded-lg border">
              <div className="text-2xl font-bold text-slate-800">{errors.length}</div>
              <div className="text-xs text-slate-500">Total Errors</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border">
              <div className="text-2xl font-bold text-red-600">{productErrors.length}</div>
              <div className="text-xs text-slate-500">Product Issues</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border">
              <div className="text-2xl font-bold text-orange-600">{shippingErrors.length}</div>
              <div className="text-xs text-slate-500">Shipping Issues</div>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border">
              <div className="text-2xl font-bold text-yellow-600">{weightErrors.length + countryErrors.length}</div>
              <div className="text-xs text-slate-500">Weight/Country</div>
            </div>
          </div>

          {/* Filters Applied */}
          {filters && (filters.date_from || filters.date_to || filters.month || filters.year) && (
            <div className="px-6 py-2 bg-amber-50 text-amber-800 text-sm border-b">
              <strong>Filters:</strong>
              {filters.date_from && ` From: ${filters.date_from}`}
              {filters.date_to && ` To: ${filters.date_to}`}
              {filters.month && ` Month: ${filters.month}`}
              {filters.year && ` Year: 20${filters.year}`}
            </div>
          )}

          {/* Data Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 print:bg-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">Tracking</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">Client</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">Product</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600">Error Details</th>
                  <th className="px-4 py-3 text-center font-semibold text-slate-600">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {errors.map((error, idx) => (
                  <tr key={idx} className="hover:bg-slate-50 print:hover:bg-transparent">
                    <td className="px-4 py-3 font-mono text-xs">{error.tracking || '-'}</td>
                    <td className="px-4 py-3">
                      <span className="font-medium">{error.client_code || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="max-w-[200px] truncate">{error.second_name || error.product || '-'}</span>
                        {error.product_count > 1 && (
                          <Badge variant="secondary" className="text-[10px]">+{error.product_count - 1}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-red-600">
                      <div className="space-y-0.5">
                        {(error.errors || []).slice(0, 2).map((err, i) => (
                          <div key={i} className="text-xs truncate max-w-[300px]">• {err}</div>
                        ))}
                        {(error.errors || []).length > 2 && (
                          <div className="text-xs text-slate-400">+{error.errors.length - 2} more</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">{formatDMY(error.order_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="px-6 py-3 text-center text-xs text-slate-400 border-t">
            EximFlow - Costing Error Report | Page 1 of 1
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintPreviewPage;
